import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { 
  IoArrowBack, IoPawOutline, IoPaw, IoSearchOutline,
  IoCallOutline, IoPersonOutline, IoHomeOutline, IoClose
} from 'react-icons/io5';
import './PadronMascotasScreen.css';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import SafeAreaInsets from '../components/Shared/SafeAreaInsets';

function PadronMascotasScreen() {
  const navigate = useNavigate();
  const { userData } = useAuth();

  // Data states
  const [mascotas, setMascotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal contact state
  const [selectedOwner, setSelectedOwner] = useState(null);

  // Fetch all pets in the condominium
  useEffect(() => {
    if (!userData?.condominioId) return;

    const q = query(
      collection(db, "mascotas"),
      where("condominioId", "==", userData.condominioId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort by pet name
      list.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
      setMascotas(list);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching census pets:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userData]);

  // Filtering logic
  const filteredMascotas = mascotas.filter(pet => {
    const term = searchTerm.toLowerCase();
    const nombre = (pet.nombre || '').toLowerCase();
    const tipo = (pet.tipo || '').toLowerCase();
    const raza = (pet.raza || '').toLowerCase();
    const unidad = (pet.unidad || '').toLowerCase();
    const dueño = (pet.nombreDueño || '').toLowerCase();
    const caracteristicas = (pet.caracteristicas || '').toLowerCase();

    return nombre.includes(term) || 
           tipo.includes(term) || 
           raza.includes(term) || 
           unidad.includes(term) || 
           dueño.includes(term) ||
           caracteristicas.includes(term);
  });

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/gestion-usuarios" />

      <main className="ks-main">
        <SafeAreaInsets top={false} bottom={true} />

        <div className="ks-header-sticky-wrapper">
          <KondoBrandHeader showBack={false} />
        </div>

        <div className="ks-scroll-area">
          {/* Header Hero Banner */}
          <div className="muro-header-stitch">
            <div className="muro-hero-card">
              <div className="muro-hero-content">
                <h1>Padrón de Mascotas</h1>
                <p>Censo general de animales del fraccionamiento</p>
              </div>
              <IoPawOutline className="muro-hero-icon-decor" />
            </div>
          </div>

          <div className="padron-mascotas-container">

            {/* Search Box */}
            <div className="search-bar-premium">
              <IoSearchOutline size={20} className="search-icon" />
              <input 
                type="text" 
                placeholder="Buscar por nombre, raza, dueño, color o casa..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                className="search-input"
              />
            </div>

            {/* List census */}
            <div className="padron-mascotas-list-section">
              {loading && (
                <div className="empty-state">
                  <p>Cargando padrón...</p>
                </div>
              )}

              {!loading && filteredMascotas.length === 0 && (
                <div className="empty-state" style={{ background: '#fff', borderRadius: '16px', padding: '40px 20px', border: '1px solid #e2e8f0' }}>
                  <IoPawOutline size={48} style={{ opacity: 0.3, marginBottom: 10, color: '#f97316' }} />
                  <p style={{ color: '#4b5563', fontSize: '1rem' }}>No se encontraron mascotas.</p>
                  <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginTop: '4px' }}>Intenta buscando con otros términos.</p>
                </div>
              )}

              {!loading && filteredMascotas.length > 0 && (
                <div className="padron-grid">
                  {filteredMascotas.map(pet => (
                    <div key={pet.id} className="padron-card">
                      {pet.fotoUrl ? (
                        <img src={pet.fotoUrl} alt={pet.nombre} className="padron-card-img" />
                      ) : (
                        <div className="padron-card-placeholder">
                          <IoPaw size={44} />
                        </div>
                      )}

                      <div className="padron-card-details">
                        <div className="padron-card-header-row">
                          <div>
                            <h3>{pet.nombre}</h3>
                            <span className="pet-type-tag">{pet.tipo} {pet.raza ? `(${pet.raza})` : ''}</span>
                          </div>
                          <span className="pet-unit-tag">Casa {pet.unidad || 'N/A'}</span>
                        </div>

                        <div className="pet-additional-data">
                          {pet.edad && <span><strong>Edad:</strong> {pet.edad}</span>}
                          <span><strong>Género:</strong> {pet.genero || 'N/A'}</span>
                        </div>

                        {pet.caracteristicas && (
                          <p className="pet-features-text">
                            <strong>Rasgos:</strong> {pet.caracteristicas}
                          </p>
                        )}

                        <div className="padron-card-owner-info">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <IoPersonOutline size={14} style={{ color: '#ea580c' }} />
                            <span>{pet.nombreDueño || 'Dueño sin registrar'}</span>
                          </div>
                        </div>

                        <button 
                          className="contact-owner-btn"
                          onClick={() => setSelectedOwner({
                            nombre: pet.nombreDueño,
                            unidad: pet.unidad,
                            telefono: pet.telefonoDueño,
                            nombreMascota: pet.nombre
                          })}
                        >
                          <IoCallOutline size={16} />
                          <span>Contactar Dueño</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </main>

      <KondoBottomNav activeRoute="/" />

      {/* --- CONTACT MODAL --- */}
      {selectedOwner && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '400px', padding: 25, borderRadius: 20 }}>
            <button className="modal-top-close-button" onClick={() => setSelectedOwner(null)}>
              <IoClose />
            </button>
            
            <h2 style={{ fontSize: '1.2rem', marginBottom: '15px' }}>Detalles de Contacto</h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <IoHomeOutline size={20} style={{ color: '#ea580c' }} />
                <div>
                  <small style={{ display: 'block', color: '#6b7280', fontSize: '0.75rem' }}>Residencia</small>
                  <strong>Unidad {selectedOwner.unidad}</strong>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <IoPersonOutline size={20} style={{ color: '#ea580c' }} />
                <div>
                  <small style={{ display: 'block', color: '#6b7280', fontSize: '0.75rem' }}>Propietario / Residente</small>
                  <strong>{selectedOwner.nombre}</strong>
                </div>
              </div>

              {selectedOwner.telefono ? (
                <a 
                  href={`tel:${selectedOwner.telefono}`}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: '8px', 
                    padding: '14px', 
                    background: '#22c55e', 
                    color: 'white', 
                    borderRadius: '12px', 
                    fontWeight: 'bold', 
                    textDecoration: 'none', 
                    boxShadow: '0 4px 10px rgba(34, 197, 94, 0.3)' 
                  }}
                >
                  <IoCallOutline size={18} />
                  Llamar al {selectedOwner.telefono}
                </a>
              ) : (
                <div style={{ padding: '12px', background: '#fee2e2', color: '#991b1b', borderRadius: '12px', fontSize: '0.85rem', textAlign: 'center' }}>
                  El dueño no tiene un teléfono registrado en su perfil.
                </div>
              )}
              
              <button 
                className="action-btn-secondary" 
                style={{ width: '100%', justifyContent: 'center', padding: '12px', background: '#64748b' }}
                onClick={() => setSelectedOwner(null)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PadronMascotasScreen;
